import Box from "@mui/material/Box";
import { Helmet } from "react-helmet-async";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AppNavbar from "../components/console/AppNavbar";
import SideMenu from "../components/console/SideMenu";
import AppTheme from "../shared-theme/AppTheme";
import { useUserEvents } from "../contexts/UserEvent";
import DataOverTime from "../components/console/DataOverTimeChart";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import * as React from "react";
import { UserEvent } from "../models/UserEvent";

export default function Analytics() {
  const { userEvents, loading, fetchUrls } = useUserEvents();
  const [selectedEvent, setSelectedEvent] = React.useState<UserEvent | null>(
    null
  );

  React.useEffect(() => {
    fetchUrls();
  }, []);

  React.useEffect(() => {
    if (!loading && userEvents.length > 0 && !selectedEvent) {
      setSelectedEvent(userEvents[0]);
    }
  }, [loading, userEvents]);

  const pageWrapper = (content: React.ReactNode) => (
    <AppTheme>
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          background:
            "radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)",
        }}
      >
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            overflow: "auto",
            pt: { xs: 10, md: 2 },
          }}
        >
          {content}
        </Box>
      </Box>
    </AppTheme>
  );

  if (loading) {
    return pageWrapper(
      <Stack
        sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}
      >
        <Typography color="white">Loading...</Typography>
      </Stack>
    );
  }

  if (!userEvents) {
    return pageWrapper(
      <Stack
        sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}
      >
        <Typography color="white">
          Error loading events. Please refresh the page.
        </Typography>
      </Stack>
    );
  }

  if (userEvents.length === 0) {
    return pageWrapper(
      <Stack
        sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}
      >
        <Typography color="white">You are not tracking any events.</Typography>
      </Stack>
    );
  }

  const trackedUrl = selectedEvent?.trackedUrls?.[0]?.url || "";
  const trackedQuantity = selectedEvent?.trackedUrls?.[0]?.quantity || 1;
  const trackedFilter = selectedEvent?.trackedUrls?.[0]?.sectionFilter || [];

  return pageWrapper(
    <Stack
      spacing={4}
      sx={{
        alignItems: "center",
        mx: 3,
        pb: 5,
      }}
    >
      <>
        {/* Meta tags for SEO */}
        <Helmet>
          <title>Analytics | Event Spy</title>
        </Helmet>
      </>
      <Typography
        variant="h4"
        sx={{
          width: "100%",
          color: "white",
          mb: 2,
        }}
      >
        Analytics
      </Typography>
      <FormControl fullWidth>
        <InputLabel
          id="event-select-label"
          sx={{
            top: "-6px",
            "&.Mui-focused": {
              top: "0px",
            },
            "&.MuiInputLabel-shrink": {
              top: "-4px",
              color: "white",
            },
          }}
        >
          Select Event
        </InputLabel>
        <Select
          labelId="event-select-label"
          value={selectedEvent?.id || ""}
          label="Select Event"
          onChange={(e) => {
            const event = userEvents.find((ev) => ev.id === e.target.value);
            if (event) setSelectedEvent(event);
          }}
        >
          {userEvents.map((event) => (
            <MenuItem key={event.id} value={event.id}>
              {event.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <DataOverTime
        url={trackedUrl}
        quantity={trackedQuantity}
        sectionFilter={trackedFilter}
        mode="price"
      />
      {(trackedUrl.includes("seatgeek") ||
        trackedUrl.includes("tickpick") ||
        trackedUrl.includes("vividseats")) && (
        <DataOverTime
          url={trackedUrl}
          quantity={trackedQuantity}
          sectionFilter={trackedFilter}
          mode="quantity"
        />
      )}
    </Stack>
  );
}
